import   React                  ,
       { useEffect              ,
         useState               }  from 'react';

import   classNames                from 'classnames';

import   styles                    from './UserSelector.module.scss';

import { User                   }  from '../../../dataTypes/userManagment/types';

import { groupData              ,
         GroupedData            ,
         completeGroupData      ,
         initializedGroupedData }  from '../../../dataTypes/dataSetManagment/regroupData';

import { convertDateToString    ,
         convertStringToDate    }  from '../../../dataTypes/timeManagment/utils';

import   Select                    from '../../widget/inputs/Select';
import   Field                     from '../../widget/inputs/Field';
import   Icon                      from '../../widget/images/Icon';
import   Trigger                   from '../../widget/actuators/Trigger';
import   TabularDisplay            from '../../widget/dataDisplay/tables/TabularDisplay';
import   Box                       from '../../widget/windows/Box'

import   UserSheet                 from './UserSheet'

export interface UserSelectorProps {
  onCancel?        : () => void | null;
  onSelect?        : (user: User) => void | null;
  userSet          : User[];
  componentWidth?  : number; // par défaut 1210
  componentHeight? : number; // par défaut 626
  originalFilter?  : string[];
}

const SORTING_CRITERIA = [ 'Aucun tri'        , 'Par ordre alphabétique' ] as const;
const GROUP_TYPES      = [ 'Date de création' , 'Ordre alphabétique'     ] as const;

type SortingCriteria = (typeof SORTING_CRITERIA)[number];
type GroupType       = (typeof GROUP_TYPES)[number];

export const UserSelector: React.FC<UserSelectorProps> = ({ onCancel               ,
                                                            onSelect               ,
                                                            userSet                ,
                                                            componentWidth  = 1210 ,
                                                            componentHeight =  626 ,
                                                            originalFilter  =   [] }) => {

  const [ originalUserSet , setOriginalUserSet ] = useState<User[]>(userSet);
  const [ filteredUserSet , setFilteredUserSet ] = useState<User[]>(userSet);
  const [ groupedData     , setGroupedData     ] = useState<GroupedData<User>> ( initializedGroupedData<User>() );

  // Tri/filtre UI
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>('Aucun tri');
  const [filter, setFilter] = useState('');

  // Grouping (par défaut par date de création)
  const [groupType, setGroupType] = useState<GroupType>('Date de création');

  const [showUserSheet, setShowUserSheet ] = useState(false);


  // Synchroniser quand la prop userSet change
  useEffect(() => {
    setOriginalUserSet(userSet);
  }, [userSet]);

  // Filtrage + tri
  useEffect(() => {
    if (!originalUserSet?.length) {
      setFilteredUserSet([]);
      return;
    }

    const needle = filter.trim().toLowerCase();
    let next = originalUserSet.filter((u) => {
      if (!needle) return true;
      return (
        u.firstname.toLowerCase().includes(needle) ||
        u.lastname.toLowerCase().includes(needle)
      );
    });

    if (sortingCriteria === 'Par ordre alphabétique') {
      next = [...next].sort((a, b) => {
        const la = (a.lastname || '').toString();
        const lb = (b.lastname || '').toString();
        const cmpLast = la.localeCompare(lb, undefined, { sensitivity: 'base' });
        if (cmpLast !== 0) return cmpLast;
        return (a.firstname || '').localeCompare(b.firstname || '', undefined, {
          sensitivity: 'base',
        });
      });
      // si on trie alpha, on regroupe aussi par lettre pour cohérence
      setGroupType('Ordre alphabétique');
    } else {
      setGroupType('Date de création');
    }

    setFilteredUserSet(next);
  }, [originalUserSet, filter, sortingCriteria]);

  // Regroupement (recalculer quand la liste filtrée ou le type de groupe change)
  useEffect(() => {
    const groupedDataLocal = groupData<User>(
      filteredUserSet,
      getGroupKey(groupType)
    );

    const completedGroupDataLocal = (() => {
      switch (groupType) {
        case 'Ordre alphabétique':
          return completeGroupData<User>(
            groupedDataLocal,
            '',
            undefined,
            undefined,
            'letter'
          );
        case 'Date de création':
        default:
          return completeGroupData<User>(groupedDataLocal, '1 jour');
      }
    })();

    setGroupedData(completedGroupDataLocal);
  }, [filteredUserSet, groupType]);

  const getGroupKey = (type: GroupType) => {
    switch (type) {
      case 'Ordre alphabétique':
        return (user: User) => {
          const match = (user.lastname || '').match(/[a-zA-Z]/);
          const letter = match ? match[0].toUpperCase() : '#';
          return {
            key: letter,
            renderer: (val: string) => val,
          };
        };
      case 'Date de création':
      default:
        return (user: User) => ({
          key: convertDateToString(user.creationDate, 'yyyy/MM/dd'),
          renderer: (val: string) =>
            `${convertDateToString(convertStringToDate(val, 'yyyy/MM/dd'), 'dd/MM/yyyy')}`,
        });
    }
  };

  const handleRowClick = (element: User) => {
    onSelect?.(element);
  };

  const renderUserTable = () => {
    const columns = [
      { header: 'Id', accessor: 'id' as keyof User },
      { header: 'Prénom', accessor: 'firstname' as keyof User },
      { header: 'Nom', accessor: 'lastname' as keyof User },
      { header: 'Email', accessor: 'email' as keyof User },
    ];

    return (
      <div className={styles.tableContainer}>
        <TabularDisplay
          groupedData={groupedData}
          columns={columns}
          hideEmptyGroups
          showGroupHeaders={false}
          onRowClick={handleRowClick}
        />
      </div>
    );
  };

  const handleNewUserCreationClick = () => {

    setShowUserSheet (true)
  }

  return (

           <div className  = {classNames(styles.root)}
                style      = {{ width: componentWidth, height: componentHeight }}
                role       = "dialog"
                aria-modal
                aria-label = "Sélecteur d'utilisateur" >

             {
              showUserSheet &&
              <Box>
                <UserSheet />
              </Box>
             }

             <div className = {classNames(styles.close_button)}>
               <Icon name       = "cross"
                     onClick    = {onCancel}
                     color      = "#000000"
                     hoverColor = "#FFFF00" />
             </div>

             <div className={classNames(styles.upper_bar)}>
               <Select choices  = {Array.from(SORTING_CRITERIA)}
                       value    = {sortingCriteria}
                       onChange = {(e: React.ChangeEvent<HTMLSelectElement>) => setSortingCriteria(e.target.value as SortingCriteria) } />
               <Field type     = "text"
                      value    ={filter}
                      onChange ={(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value) } />
               <div className = {classNames(styles.creationButton)}>
                 <Trigger name    = "Créer un utilisateur"
                          onClick = {handleNewUserCreationClick}
                          variant = "tailwind2" />
               </div>
             </div>

             {/* Zone scrollable contenant le tableau (ne dépasse plus) */}
             <div className = {classNames(styles.user_grid)}>
               {renderUserTable()}
             </div>
           </div>
         );
};

export default UserSelector;
